import { queryOptions } from "@tanstack/react-query";
import { apiClient, unwrap } from "../../api/client";
import type { components } from "../../api/generated/vision-api";
import { allPages } from "../devices/queries";

const api = () => apiClient("customers");

/** My cases (WEB-SUP-CASE-03): read again when the window gets focus. */
export const casesQuery = (orgId: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/cases"],
    queryFn: () => allPages((cursor) => unwrap(api().GET("/v1/cases", { params: { query: { limit: 100, cursor } } }))),
    refetchOnWindowFocus: true,
    enabled: !!orgId,
  });

/** A case page refreshes when it regains focus (WEB-SUP-CASE-04); live waits for a live message in the contract. */
export const caseQuery = (orgId: string, caseId: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/cases", caseId],
    queryFn: () => unwrap(api().GET("/v1/cases/{case_id}", { params: { path: { case_id: caseId } } })),
    refetchOnWindowFocus: true,
  });

export const messagesQuery = (orgId: string, caseId: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/cases", caseId, "messages"],
    queryFn: () =>
      allPages((cursor) =>
        unwrap(
          api().GET("/v1/cases/{case_id}/messages", {
            params: { path: { case_id: caseId }, query: { limit: 100, cursor } },
          }),
        ),
      ),
    refetchOnWindowFocus: true,
  });

export function openCase(body: components["schemas"]["SupportCaseCreate"]) {
  return unwrap(api().POST("/v1/cases", { body }));
}

export function postMessage(caseId: string, text: string) {
  return unwrap(
    api().POST("/v1/cases/{case_id}/messages", { params: { path: { case_id: caseId } }, body: { body: text } }),
  );
}

/** Withdraw the case's grant (WEB-SUP-GRT-03, SUP-33): support's view closes at once; the case stays open. */
export async function withdrawGrant(caseId: string) {
  await unwrap(api().DELETE("/v1/cases/{case_id}/grant", { params: { path: { case_id: caseId } } }));
}

/** Give, extend or raise the case's grant (WEB-SUP-GRT-03): a new level or length replaces the old one, from now. */
export function setGrant(caseId: string, grant: components["schemas"]["SupportGrantInput"]) {
  return unwrap(api().PUT("/v1/cases/{case_id}/grant", { params: { path: { case_id: caseId } }, body: grant }));
}

/** Add devices to a case (WEB-SUP-GRT-05): support sees them only once access is given for them too (SUP-36). */
export function addCaseDevices(caseId: string, deviceIds: string[]) {
  return unwrap(
    api().POST("/v1/cases/{case_id}/devices", {
      params: { path: { case_id: caseId } },
      body: { device_ids: deviceIds },
    }),
  );
}

/** Requests waiting for an Owner or Admin (WEB-SUP-REQ-01); read again on focus and every minute for the count. */
export const supportRequestsQuery = (orgId: string, enabled: boolean) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/support-requests"],
    queryFn: () =>
      allPages((cursor) => unwrap(api().GET("/v1/support-requests", { params: { query: { limit: 100, cursor } } }))),
    enabled: !!orgId && enabled,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });

/** Allow with the access given, or deny (WEB-SUP-REQ-02). Only this answer grants access (SUP-24). */
export function answerRequest(id: string, grant: components["schemas"]["SupportGrantInput"] | null) {
  return unwrap(
    api().POST("/v1/support-requests/{request_id}/answer", {
      params: { path: { request_id: id } },
      body: grant ? { allow: true, grant } : { allow: false },
    }),
  );
}

/** Every active support grant of the organisation (WEB-SUP-GRT-04), for Owners and Admins. */
export const supportGrantsQuery = (orgId: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/support-grants"],
    queryFn: () =>
      allPages((cursor) => unwrap(api().GET("/v1/support-grants", { params: { query: { limit: 100, cursor } } }))),
    enabled: !!orgId,
    refetchOnWindowFocus: true,
  });

export async function revokeGrant(id: string) {
  await unwrap(api().DELETE("/v1/support-grants/{grant_id}", { params: { path: { grant_id: id } } }));
}

/** `confirm` closes a resolved case (It's fixed); `reopen` brings it back within 7 days (Still a problem). */
export function actOnCase(caseId: string, action: "confirm" | "reopen") {
  return unwrap(api().POST("/v1/cases/{case_id}/{action}", { params: { path: { case_id: caseId, action } } }));
}
